module Client.Page.Admin.MapSensorList

open System.Collections.Generic
open Client
open Client.Components
open Client.Components.Select
open Client.Domain
open Client.DomainTypes
open Client.DomainTypes.Select
open Client.Infrastructure
open Client.Infrastructure.Api
open Client.DomainTypes.Msg
open Client.Forms
open Elmish
open Fable.FontAwesome
open Fable.React.Props
open Fulma
open Fulma.Extensions.Wikiki
open Shared
open Shared.Dto
open Shared.Dto.Dto
open Shared.DtoTypes.PhysicalSensor
open Shared.Infrastructure
open Fable.React
open Shared.Dto.User
open Shared.WithLastDate
open Thoth.Elmish
open System
open Zanaptak.TypedCssClasses

Fable.Core.JsInterop.importAll "bulma-tooltip"

type FA =
    CssClasses<"https://stackpath.bootstrapcdn.com/font-awesome/4.7.0/css/font-awesome.min.css", Naming.PascalCase>

type Modal =
    | ChoosePhysicalSensor of ChoosePhysicalSensor.Model
    | MapSensor of MapSensor.Model


type DataModel = {
    MapSensors: WithOptionalLastDate<IdValue<MapSensorDto>> list
    FilteredMapSensors: WithOptionalLastDate<IdValue<MapSensorDto>> list
    PhysicalSensors: IDictionary<int, ConfiguredPhysicalSensor>
    Users: IDictionary<int, UserDto>
    Modal: Modal option
    Session: UserSession
    DisplayFilters: bool
    SelectedUserFilter: UserFilter.State
    NameFilter: string option
    PhysicalSensorFilter: string option
}

type LoadingModel = {
    Session: UserSession
    MySensSensors: WithOptionalLastDate<IdValue<MapSensorDto>> list option
    PhysicalSensors: ConfiguredPhysicalSensor list option
    Users: UserDto list option
}

let modelLoadingToData (model: LoadingModel) : DataModel option =
    Option.map3
        (fun physicalSensors mapSensors (users: UserDto list) -> {
            MapSensors = mapSensors
            FilteredMapSensors = mapSensors
            Session = model.Session
            PhysicalSensors = physicalSensors |> List.map (fun sensor -> sensor.BaseData.Id, sensor) |> dict
            Users = List.map (fun (user: UserDto) -> user.Id, user) users |> dict
            Modal = None
            DisplayFilters = false
            SelectedUserFilter = UserFilter.NoFilter
            NameFilter = None
            PhysicalSensorFilter = None
        })
        model.PhysicalSensors
        model.MySensSensors
        model.Users

let private belongsToUser (user: UserDto) (sensor: MapSensorDto) =
    match (MapSensor.getBaseData sensor).UserId with
    | Some id -> id = user.Id
    | None -> false

let private filterSensor
    (viablePhysicalSensors: int list option)
    (model: DataModel)
    (sensor: WithOptionalLastDate<IdValue<MapSensorDto>>)
    : bool =
    let userFilterMatch =
        UserFilter.applyFilter (MapSensor.getBaseData sensor.Value.Value).UserId model.SelectedUserFilter

    let nameFilterMatch =
        match model.NameFilter with
        | Some name -> String.containsIgnoreCase (MapSensor.getBaseData sensor.Value.Value).Name name
        | None -> true

    let physicalSensorFilterMatch =
        match viablePhysicalSensors, (MapSensor.getBaseData sensor.Value.Value).PhysicalSensorId with
        | Some physicalSensors, Some physicalSensorId -> List.contains physicalSensorId physicalSensors
        | Some _, None -> false
        | None, _ -> true

    userFilterMatch && nameFilterMatch && physicalSensorFilterMatch

let private updateFilteredSensors (model: DataModel) : DataModel =
    let viablePhysicalSensors =
        model.PhysicalSensorFilter
        |> Option.map (fun physicalSensorFilter ->
            List.filter
                (fun physical -> String.containsIgnoreCase physical.BaseData.Name physicalSensorFilter)
                (List.ofDictValues model.PhysicalSensors)
            |> List.map (fun physical -> physical.BaseData.Id)
        )

    { model with FilteredMapSensors = List.filter (filterSensor viablePhysicalSensors model) model.MapSensors }

type Model = Loadable<DataModel, LoadingModel>

let init (session: UserSession) =
    let requestData = {
        SessionKey = session.SessionKey
        Data = ()
    }

    let cmds =
        Cmd.batch [
            Cmd.OfAsync.perform
                api.getConfiguredTtnSensors
                ()
                (MapSensorListMsg.PhysicalSensorListReceived >> MapSensorList)
            Cmd.OfAsync.perform
                api.getAllMySensSensorsWithOptionalLastDate
                ()
                (MapSensorListMsg.MapSensorListReceived >> MapSensorList)
            Cmd.OfAsync.perform api.getAllUsers requestData (MapSensorListMsg.UsersReceived >> MapSensorList)
        ]

    Loadable.Loading {
        MySensSensors = None
        Session = session
        PhysicalSensors = None
        Users = None
    },
    cmds

let updateRequestRunningModalState (model: DataModel) (requestRunning: bool) =
    let newModalModel =
        match model.Modal with
        | Some(MapSensor modalModel) ->
            let newModel = { modalModel with RequestRunning = requestRunning }

            Some(MapSensor newModel)
        | _ -> model.Modal

    { model with Modal = newModalModel }

let getSortKey (sensor: WithOptionalLastDate<IdValue<MapSensorDto>>) =
    (MapSensor.getBaseData sensor.Value.Value).Name

let update (msg: MapSensorListMsg) (model: Model) : Model * Cmd<Msg> =
    match msg, model with
    | MapSensorListMsg.PhysicalSensorListReceived sensors, Loadable.Loading loadingModel ->
        let updatedLoadingModel = { loadingModel with PhysicalSensors = Some sensors }

        let newModel =
            modelLoadingToData updatedLoadingModel
            |> Option.map Loadable.Data
            |> Option.defaultValue (Loadable.Loading updatedLoadingModel)

        newModel, Cmd.none
    | MapSensorListMsg.MapSensorListReceived sensors, Loadable.Loading loadingModel ->
        let updatedLoadingModel = { loadingModel with MySensSensors = Some(List.sortBy getSortKey sensors) }

        let newModel =
            modelLoadingToData updatedLoadingModel
            |> Option.map Loadable.Data
            |> Option.defaultValue (Loadable.Loading updatedLoadingModel)

        newModel, Cmd.none
    | MapSensorListMsg.UsersReceived maybeUsers, Loadable.Loading loadingModel ->
        match maybeUsers with
        | Result.Ok users ->
            let updatedLoadingModel = { loadingModel with Users = Some users }

            let newModel =
                modelLoadingToData updatedLoadingModel
                |> Option.map Loadable.Data
                |> Option.defaultValue (Loadable.Loading updatedLoadingModel)

            newModel, Cmd.none
        | Result.Error msg ->
            let errorMessage =
                match msg with
                | AuthErr Unauthenticated -> "Sie sind nicht angemeldet? Laden Sie die Seite neu"
                | AuthErr Unauthorized -> "Sie düfen nicht die Kartensensoren auflisten"
                | CustomErr error -> error

            let toastCmd =
                Toast.create errorMessage |> Toast.title "Fehler beim User laden" |> Toast.error

            model, toastCmd

    | MapSensorListMsg.CloseModal, Loadable.Data data -> Loadable.Data { data with Modal = None }, Cmd.none
    | MapSensorListMsg.OpenModal maybeSensor, Loadable.Data data ->
        let modal, cmd =
            match maybeSensor with
            | Some sensor ->
                let modalModel =
                    MapSensor.initExisting
                        (List.ofDictValues data.PhysicalSensors)
                        (List.ofDictValues data.Users)
                        sensor

                MapSensor modalModel, Cmd.none
            | None ->
                let modalModel, cmd = ChoosePhysicalSensor.init data.Session

                ChoosePhysicalSensor modalModel, cmd

        Loadable.Data { data with Modal = Some modal },

        Cmd.map MapSensorList cmd
    | MapSensorListMsg.SensorUpdated response, Loadable.Data data ->
        match response with
        | Ok(Some updatedSensor) ->
            let toastCmd = Toast.create "Der Sensor wurde erfolgreich geändert" |> Toast.success

            let newSensorList =
                List.filteredMap
                    (fun (sensor: WithOptionalLastDate<IdValue<MapSensorDto>>) -> sensor.Value.Id = updatedSensor.Id)
                    (fun sensor -> { sensor with Value = updatedSensor })
                    data.MapSensors

            let newDataModel =
                {
                    data with
                        MapSensors = newSensorList
                        Modal = None
                }
                |> updateFilteredSensors
                |> Loadable.Data

            newDataModel, toastCmd
        | _ ->
            let toastCmd = Toast.create "Die Änderung ist fehlgeschlagen" |> Toast.error

            Loadable.Data(updateRequestRunningModalState data false), toastCmd
    | MapSensorListMsg.SensorCreated response, Loadable.Data data ->
        match response with
        | Ok(Some newSensor) ->
            let toastCmd = Toast.create "Der Sensor wurde erfolgreich erstellt" |> Toast.success

            let newSensor = {
                LastDate = None
                Value = newSensor
            }

            let getSensorName =
                fun (sensor: WithOptionalLastDate<IdValue<MapSensorDto>>) ->
                    (MapSensor.getBaseData sensor.Value.Value).Name

            let newSensorList = (newSensor :: data.MapSensors) |> List.sortBy getSensorName


            let newDataModel =
                {
                    data with
                        MapSensors = newSensorList
                        Modal = None
                }
                |> updateFilteredSensors
                |> Loadable.Data

            newDataModel, toastCmd
        | _ ->
            let toastCmd = Toast.create "Das Erstellen ist fehlgeschlagen" |> Toast.error

            Loadable.Data(updateRequestRunningModalState data false), toastCmd
    | MapSensorListMsg.UpdateSensor sensor, Loadable.Data data ->
        let requestData = {
            Data = sensor
            SessionKey = data.Session.SessionKey
        }

        Loadable.Data(updateRequestRunningModalState data true),
        Cmd.OfAsync.perform api.updateMapSensor requestData (MapSensorListMsg.SensorUpdated >> MapSensorList)
    | MapSensorListMsg.CreateSensor newSensor, Loadable.Data data ->
        let requestData = {
            Data = newSensor
            SessionKey = data.Session.SessionKey
        }

        Loadable.Data(updateRequestRunningModalState data true),
        Cmd.OfAsync.perform api.createMapSensor requestData (MapSensorListMsg.SensorCreated >> MapSensorList)
    | MapSensorListMsg.DeleteSensor id, _ ->
        model,
        Cmd.OfAsync.perform
            api.deleteMySensSensor
            id
            ((fun success -> id, success) >> MapSensorListMsg.SensorDeleted >> MapSensorList)
    | MapSensorListMsg.SensorDeleted(deletedId, success), Loadable.Data data ->
        if success then
            let toastCmd = Toast.create "Sensor erfolgreich gelöscht" |> Toast.success

            let newSensorList =
                List.filter
                    (fun (sensor: WithOptionalLastDate<IdValue<_>>) -> sensor.Value.Id <> deletedId)
                    data.MapSensors

            let newDataModel =
                {
                    data with
                        MapSensors = newSensorList
                        Modal = None
                }
                |> updateFilteredSensors
                |> Loadable.Data

            newDataModel, toastCmd
        else
            let toastCmd = Toast.create "Fehler beim Löschen des Sensors" |> Toast.error

            model, toastCmd
    | MapSensorListMsg.ChoosePhysicalSensorForm choosePhysicalSensorFormMsg, Loadable.Data data ->
        match choosePhysicalSensorFormMsg, data.Modal with
        | Forward sensor, _ ->
            let model =
                MapSensor.initNew (List.ofDictValues data.PhysicalSensors) (List.ofDictValues data.Users) sensor

            Loadable.Data { data with Modal = Some(MapSensor model) }, Cmd.none
        | _, Some(ChoosePhysicalSensor modalData) ->
            ChoosePhysicalSensor.update choosePhysicalSensorFormMsg modalData
            |> fun (modal, cmd) ->
                Loadable.Data { data with Modal = Some(ChoosePhysicalSensor modal) }, Cmd.map MapSensorList cmd
        | _, _ -> model, Cmd.none
    | MapSensorListMsg.UserFilterChanged userFilter, Loadable.Data dataModel ->
        let newDataModel = { dataModel with SelectedUserFilter = userFilter }

        Loadable.Data(updateFilteredSensors newDataModel), Cmd.none
    | MapSensorListMsg.ToggleFilterDisplay, Loadable.Data dataModel ->
        Loadable.Data { dataModel with DisplayFilters = not dataModel.DisplayFilters }, Cmd.none
    | MapSensorListMsg.NameFilterChanged name, Loadable.Data dataModel ->
        let newDataModel = { dataModel with NameFilter = name } |> updateFilteredSensors

        Loadable.Data newDataModel, Cmd.none
    | MapSensorListMsg.PhysicalSensorFilterChanged physicalSensorName, Loadable.Data dataModel ->
        let newDataModel =
            { dataModel with PhysicalSensorFilter = physicalSensorName }
            |> updateFilteredSensors

        Loadable.Data newDataModel, Cmd.none
    | _, Loadable.Data data ->
        let result =
            match data.Modal with
            | Some(MapSensor modal) -> MapSensor.update msg modal |> (fun (modal, cmd) -> Some(MapSensor modal), cmd)
            | _ -> None, Cmd.none

        Loadable.Data { data with Modal = fst result }, snd result
    | _, _ -> model, Cmd.none

let private getTotalMinutesLimit (sensorType: SensorModel) =
    match sensorType with
    | WSC1L -> 15.0
    | LHT65
    | LSN50v2_S31
    | S31_LS
    | S31_LB
    | LSN50v2_Rain_01mm
    | LSN50v2_Rain_02mm
    | SN50v3_LS_Rain_01mm
    | SN50v3_LS_Rain_02mm
    | SN50v3_LB_Rain_01mm
    | SN50v3_LB_Rain_02mm
    | LSN50v2_Wind
    | SN50v3_LS_Wind
    | SN50v3_LB_Wind
    | DDS20_LB
    | DDS20_LS
    | LDDS75
    | DDS75_LB
    | DDS75_LS -> 30.0
    | LSE01
    | SE01_LS
    | SE01_LB
    | SE0X_LB
    | LLMS01
    | LSPH01
    | SPH01_LB
    | SPH01_LS
    | RS485_LB_ZFS_02
    | RS485_LS_ZFS_02
    | PS_LB _
    | PS_LS _
    | WQS_LB
    | WQS_LS -> 50.0

let getIconForSensor
    (maybeTtnSensor: ConfiguredPhysicalSensor option)
    (sensor: WithOptionalLastDate<IdValue<MapSensorDto>>)
    =
    let toolTip, icon =
        match maybeTtnSensor with
        | Some ttnSensor ->
            let totalTimeLimit = getTotalMinutesLimit ttnSensor.Configuration

            let isOldAge =
                match sensor.LastDate with
                | None -> true
                | Some lastDate ->
                    let elapsedMinutes = (DateTimeOffset.UtcNow - lastDate).TotalMinutes

                    elapsedMinutes > totalTimeLimit

            match (MapSensor.getBaseData sensor.Value.Value).State, isOldAge with
            | Disabled, _ -> Some "Sensor ist deaktiviert", Fa.Solid.Ban
            | Active, true -> Some "Daten sind veraltet", Fa.Solid.Hourglass
            | Active, false -> None, Fa.Solid.Check
        | None -> Some "Kein physikalischer Sensor zugeordnet", Fa.Solid.QuestionCircle


    td [
        match toolTip with
        | Some text ->
            classList [ (Tooltip.ClassName, true) ]
            Tooltip.dataTooltip text
        | None -> ()
    ] [ Fa.span [ icon ] [] ]


let private createUserSettingsPageDropDownItem (dispatch: Msg -> unit) (sensorId: int) =
    let route = Route.UserDefinedMapSensorProperties sensorId

    DropDownList.createGoToRouteItem dispatch Fa.Solid.Cog "Einstellungen" route

let private createDetailPageDropDownItem (dispatch: Msg -> unit) (sensorId: int) =
    let route = Route.MySensSensor sensorId

    DropDownList.createGoToRouteItem dispatch Fa.Solid.Search "Detail-Seite" route

let private createDataPacketsDropDownItem (dispatch: Msg -> unit) (sensorId: int) =
    let route = Route.ReceivedSensorPackets sensorId

    DropDownList.createGoToRouteItem dispatch Fa.Solid.Box "Empfangene Daten" route

let private createSensorDropdownMenu
    (dispatch: Msg -> unit)
    (sensor: WithOptionalLastDate<IdValue<MapSensorDto>>)
    (position: Location)
    =
    let createItem = DropDownList.createItem

    let userDefinedSettingsPageItem =
        createUserSettingsPageDropDownItem dispatch sensor.Value.Id

    let detailPageItem = createDetailPageDropDownItem dispatch sensor.Value.Id
    let dataPacketsPageItem = createDataPacketsDropDownItem dispatch sensor.Value.Id

    let dropDownItems = [
        createItem
            Fa.Solid.Edit
            "Bearbeiten"
            (fun _ -> MapSensorListMsg.OpenModal(Some sensor.Value) |> MapSensorList |> dispatch)
        userDefinedSettingsPageItem
        detailPageItem
        dataPacketsPageItem
        createItem
            Fa.Solid.Globe
            "Zur Karte"
            (fun _ -> Clickable.goToRouteMsg (Route.SensorMap(Some position)) |> dispatch)
        createItem
            Fa.Solid.TrashAlt
            "Löschen"
            (fun _ -> MapSensorListMsg.DeleteSensor sensor.Value.Id |> MapSensorList |> dispatch)
    ]

    DropDownList.createContextMenu dropDownItems

let sensorToRow
    dispatch
    (physicalSensors: IDictionary<int, ConfiguredPhysicalSensor>)
    (users: IDictionary<int, UserDto>)
    (index: int)
    (sensor: WithOptionalLastDate<IdValue<MapSensorDto>>)
    =
    let baseData = (MapSensor.getBaseData sensor.Value.Value)

    let physicalSensor =
        baseData.PhysicalSensorId |> Option.bind (Dict.tryGetValue physicalSensors)

    let icon = getIconForSensor physicalSensor sensor

    let lastDateString =
        sensor.LastDate
        |> Option.map TimeSpan.createTimeSinceString
        |> Option.defaultValue ""

    let lastAssignmentDateString =
        baseData.LastAssignmentDate
        |> Option.map DateTime.toDateString
        |> Option.map (sprintf " (%s)")
        |> Option.defaultValue ""

    let lastDateTooltip =
        sensor.LastDate
        |> Option.map (fun date -> date.LocalDateTime)
        |> Option.map (fun date -> sprintf "%s (%s)" (DateTime.toDayMonthString date) (DateTime.toTimeString date))
        |> Option.map Tooltip.dataTooltip

    let stateSinceString =
        baseData.StateSince |> DateTimeOffset.toLocalDateTime |> DateTime.toShortString

    let lastDateCellProps =
        [
            classList [
                (Tooltip.ClassName, (Option.isSome lastDateTooltip))
                ("last_date", true)
            ]
            :> IHTMLProp
            |> Some
            Option.map (fun date -> date :> IHTMLProp) lastDateTooltip
        ]
        |> List.choose id

    let sensorHasLocation =
        if baseData.Location.Latitude <> 0 && baseData.Location.Longitude <> 0 then
            "Ja"
        else
            "Nein"

    let physicalSensorName =
        physicalSensor
        |> Option.map (fun ttnSensor -> ttnSensor.BaseData.Name)
        |> Option.defaultValue ""

    let maybeUsername =
        baseData.UserId
        |> Option.bind (Dict.tryGetValue users)
        |> Option.map getFullName

    tr [] [
        td [] [ Table.rowIndexString index ]
        icon
        td [] [ str baseData.Name ]
        td [] [ baseData.Note |> Option.defaultValue "" |> str ]
        td [] [ str sensorHasLocation ]
        td [] [ str (Bool.toUserString baseData.IsPublic) ]
        td lastDateCellProps [ str lastDateString ]
        td [] [ str physicalSensorName ]
        td [] [
            match maybeUsername with
            | Some userName ->
                Icon.icon [] [ Fa.i [ Fa.Solid.User ] [] ]
                span [] [ str userName ]
            | None -> ()

            str lastAssignmentDateString
        ]
        td [] [ str stateSinceString ]
        td [] [
            createSensorDropdownMenu dispatch sensor baseData.Location
        ]
    ]

let sensorListToTable
    dispatch
    (physicalSensors: IDictionary<int, ConfiguredPhysicalSensor>)
    (users: IDictionary<int, UserDto>)
    (sensors: WithOptionalLastDate<IdValue<MapSensorDto>> list)
    =
    Table.table [
        Table.CustomClass "table-auto"
        Table.IsBordered
        Table.IsFullWidth
        Table.IsStriped
        Table.Modifiers [
            Modifier.Spacing(Spacing.MarginBottom, Spacing.Is6)
        ]
    ] [
        thead [] [
            tr [] [
                th [] [ str "#" ]
                th [] []
                th [] [ str "Name" ]
                th [] [ str "Notizen" ]
                th [] [ str "Hat GPS-Position?" ]
                th [] [ str "Sensor ist öffentlich" ]
                th [ classList [ ("last_date", true) ] ] [ str "Letzte Aktual." ]
                th [] [ str "Physischer Sensor" ]
                th [] [ str "Benutzer" ]
                th [] [ str "Status seit" ]
                th [] []
            ]
        ]
        tbody [] (List.mapi (sensorToRow dispatch physicalSensors users) sensors)
    ]

let createNewSensorButton dispatch =
    Button.button [
        Button.Color IsLink
        Button.OnClick(fun _ -> dispatch (MapSensorList <| MapSensorListMsg.OpenModal None))
    ] [ str "Neuen Sensor erstellen" ]

let private toggleFilterDisplayButton dispatch =
    let customOptions = [
        CommonButtons.onClick (fun _ -> dispatch (MapSensorList MapSensorListMsg.ToggleFilterDisplay))
    ]

    CommonButtons.createFilterButton customOptions

let tableHeader dispatch =
    Level.level [] [
        Level.left [] []
        Level.right [] [
            Level.item [] [
                Field.div [] [
                    Control.div [] [ createNewSensorButton dispatch ]
                ]
            ]
            Level.item [] [
                Field.div [] [
                    Control.div [] [ toggleFilterDisplayButton dispatch ]
                ]
            ]
        ]
    ]

let private filtersView dispatch (data: DataModel) =
    Box.box' [ GenericOption.CustomClass "filter_box" ] [
        Columns.columns [] [
            Column.column [ Column.Width(Screen.All, Column.Is4) ] [
                Field.div [] [
                    Label.label [] [ str "Name" ]
                    Control.div [ Control.HasIconLeft ] [
                        Icon.icon [ Icon.Size IsSmall; Icon.IsLeft ] [ Fa.i [ Fa.Solid.Search ] [] ]

                        Input.text [
                            data.NameFilter |> Option.defaultValue "" |> Input.ValueOrDefault
                            Input.OnChange(fun event ->
                                event.Value |> String.toOption |> MapSensorListMsg.NameFilterChanged |> dispatch
                            )
                        ]
                    ]
                ]
            ]
            Column.column [ Column.Width(Screen.All, Column.Is4) ] [
                Field.div [] [
                    Label.label [] [ str "Physischer Sensor" ]
                    Control.div [ Control.HasIconLeft ] [
                        Icon.icon [ Icon.Size IsSmall; Icon.IsLeft ] [ Fa.i [ Fa.Solid.Search ] [] ]

                        Input.text [
                            data.PhysicalSensorFilter |> Option.defaultValue "" |> Input.ValueOrDefault
                            Input.OnChange(fun event ->
                                event.Value
                                |> String.toOption
                                |> MapSensorListMsg.PhysicalSensorFilterChanged
                                |> dispatch
                            )
                        ]
                    ]
                ]
            ]
            Column.column [ Column.Width(Screen.All, Column.Is4) ] [
                Field.div [] [
                    Label.label [] [ str "Nach Benutzer filtern" ]
                    Control.div [ Control.HasIconLeft ] [
                        UserFilter.view
                            (MapSensorListMsg.UserFilterChanged >> dispatch)
                            data.SelectedUserFilter
                            (List.ofDictValues data.Users)

                        Icon.icon [ Icon.Size IsSmall; Icon.IsLeft ] [ Fa.i [ Fa.Solid.User ] [] ]
                    ]
                ]
            ]
        ]
    ]

let private dataView dispatch (data: DataModel) =
    let closeModal = (fun _ -> dispatch (MapSensorListMsg.CloseModal |> MapSensorList))

    let table =
        sensorListToTable dispatch data.PhysicalSensors data.Users data.FilteredMapSensors

    let modal =
        match data.Modal with
        | Some(ChoosePhysicalSensor modal) -> ChoosePhysicalSensor.view (MapSensorList >> dispatch) closeModal modal
        | Some(MapSensor modal) -> MapSensor.view dispatch closeModal modal
        | None -> div [] []

    Container.container [
        Container.IsFluid
        Container.CustomClass "content-container"
    ] [
        Heading.h1 [] [ str "Karten Sensoren Liste" ]

        tableHeader dispatch

        match data.DisplayFilters with
        | true -> filtersView (MapSensorList >> dispatch) data
        | false -> ()

        (Table.scrollableTable table)
        modal
    ]

let view (model: Model) dispatch = Loadable.view (dataView dispatch) model